import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, DollarSign, ShoppingBag, Inbox, Briefcase, FolderKanban, MessageSquare, TrendingUp } from 'lucide-react';
import { adminApi } from '@/lib/api';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="admin-stat-card"
  >
    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className="text-3xl font-heading font-bold mb-1">{value}</p>
    <p className="text-muted-foreground text-sm">{label}</p>
  </motion.div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your CMS admin panel.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={`$${stats?.total_revenue?.toLocaleString() || 0}`}
          color="bg-green-500/20 text-green-400"
        />
        <StatCard
          icon={ShoppingBag}
          label="Purchases"
          value={stats?.total_purchases || 0}
          color="bg-primary/20 text-primary"
        />
        <StatCard
          icon={Inbox}
          label="Contact Messages"
          value={stats?.total_contacts || 0}
          color="bg-blue-500/20 text-blue-400"
        />
        <StatCard
          icon={Users}
          label="Unread Messages"
          value={stats?.unread_contacts || 0}
          color="bg-orange-500/20 text-orange-400"
        />
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Briefcase}
          label="Services"
          value={stats?.services_count || 0}
          color="bg-purple-500/20 text-purple-400"
        />
        <StatCard
          icon={ShoppingBag}
          label="Products"
          value={stats?.products_count || 0}
          color="bg-pink-500/20 text-pink-400"
        />
        <StatCard
          icon={FolderKanban}
          label="Portfolio Items"
          value={stats?.portfolio_count || 0}
          color="bg-cyan-500/20 text-cyan-400"
        />
        <StatCard
          icon={MessageSquare}
          label="Testimonials"
          value={stats?.testimonials_count || 0}
          color="bg-yellow-500/20 text-yellow-400"
        />
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-heading font-semibold mb-4">Quick Tips</h2>
        <div className="admin-card">
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-2">
              <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Use the sidebar navigation to manage different sections of your website.</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>All changes are saved automatically when you update content.</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Packages with completed purchases cannot be deleted to maintain data integrity.</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Click "View Site" in the top bar to preview your changes.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
